import { Controller, useWatch, type UseFormReturn } from 'react-hook-form';
import { AutocompleteMultiSelect, Label } from '@shigo-ui/ui';
import { dashboardT } from '../../../../../lib/messages';
import { ShigoRole } from '../../../../auth/permissions';
import type { AdminUserFormValue } from './UserDialog.schema';
import { translateValidationMessage } from './UserDialog.helpers';

import { useAgencyOptions } from '../../../../supply/agency/hooks';
import { dashboardErrorMessage } from '../../../../../lib/apiErrorMessages';
export function UserAgencyFields({
  form,
  disabled,
  index,
  networkName,
}: {
  form: UseFormReturn<AdminUserFormValue>;
  disabled: boolean;
  index?: number;
  networkName?: string;
}) {
  const rolesPath =
    index === undefined
      ? 'roles'
      : (`memberships.${String(index)}.roles` as `memberships.${number}.roles`);
  const name =
    index === undefined
      ? 'agencyIds'
      : (`memberships.${String(index)}.agencyIds` as `memberships.${number}.agencyIds`);
  const roles = useWatch({ control: form.control, name: rolesPath });
  const membershipNetworkId = useWatch({
    control: form.control,
    name:
      index === undefined
        ? 'networkId'
        : (`memberships.${String(index)}.networkId` as `memberships.${number}.networkId`),
  });
  const query = useAgencyOptions(membershipNetworkId, roles.includes(ShigoRole.Agency));
  const error = form.getFieldState(name, form.formState).error;
  if (!roles.includes(ShigoRole.Agency)) return null;
  const label = networkName
    ? `${networkName} ${dashboardT('users.agencies')}`
    : dashboardT('users.agencies');
  return (
    <div className="space-y-1.5">
      <Label className="text-[13px] font-semibold">
        {dashboardT('users.agencies')}
        <span className="text-destructive"> *</span>
      </Label>
      <Controller
        control={form.control}
        name={name}
        render={({ field }) => (
          <AutocompleteMultiSelect
            label={label}
            options={(query.data ?? []).map((agency) => ({ id: agency.id, label: agency.name }))}
            value={field.value}
            onValueChange={field.onChange}
            disabled={disabled || query.isError}
            loading={query.isFetching}
            placeholder={dashboardT('users.searchAgencies')}
            emptyMessage={dashboardT('users.noAgencies')}
            inlineSelectedValues={index !== undefined}
          />
        )}
      />
      {error?.message ? (
        <p className="text-destructive text-xs">{translateValidationMessage(error.message)}</p>
      ) : null}
      {query.isError ? (
        <p role="alert" className="text-destructive text-xs">
          {dashboardErrorMessage(query.error)}
        </p>
      ) : null}
    </div>
  );
}
